import { z } from 'zod';

// Message Attachment Files - files associated with specific messages
export const AttachmentFileSchema = z.object({
  id: z.string().describe('Unique identifier for this file'),
  name: z.string().optional().describe('Original filename if available'),
  base64: z.string().describe('Base64 encoded file data'),
  mimeType: z.string().default('image/png').describe('MIME type of the file'),
  uploadedAt: z.number().describe('Timestamp when file was uploaded'),
  metadata: z
    .object({
      width: z.number().optional(),
      height: z.number().optional(),
      sourceType: z.enum(['screenshot', 'upload', 'paste']).optional(),
      captureSource: z.string().optional().describe('Source of screenshot if applicable'),
    })
    .optional(),
});

export type AttachmentFile = z.infer<typeof AttachmentFileSchema>;

// Backward compatibility alias (deprecated)
export const TopicFileSchema = AttachmentFileSchema;
export type TopicFile = AttachmentFile;

// Vision analysis summary (lightweight, created once per topic)
export const VisionSummarySchema = z.object({
  id: z.string().describe('Unique identifier for this analysis'),
  topicFileIds: z.array(z.string()).describe('IDs of files this analysis covers'),
  createdAt: z.number().describe('When this analysis was performed'),
  summary: z.string().describe('Human-readable summary of what was analyzed'),
  screenType: z
    .enum([
      'dashboard',
      'patient_chart',
      'claims_form',
      'eligibility_screen',
      'billing_page',
      'other',
    ])
    .optional(),
  keyEntities: z.array(z.string()).optional().describe('Key entities found (IDs, names, etc.)'),
  importantData: z.record(z.string()).optional().describe('Important data points extracted'),
});

export type VisionSummary = z.infer<typeof VisionSummarySchema>;

// Enhanced chat message with current topic files
export const EnhancedChatMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  timestamp: z.number().optional(),
  currentTopicFiles: z
    .array(TopicFileSchema)
    .optional()
    .describe('Files that are currently active in this conversation context'),
  visionSummary: VisionSummarySchema.optional().describe(
    'Summary of vision analysis if this message involved new screen analysis'
  ),
  // OpenAI file IDs for direct API integration
  screenshotIds: z
    .array(z.string())
    .optional()
    .describe('OpenAI file IDs for screenshots attached to this message'),
  attachmentIds: z
    .array(z.string())
    .optional()
    .describe('OpenAI file IDs for attachments attached to this message'),
  // S3 attachment URLs for this message (for context in follow-up messages)
  screenshotUrls: z
    .array(z.string())
    .optional()
    .describe('S3 presigned URLs for screenshots attached to this message'),
  attachmentUrls: z
    .array(z.string())
    .optional()
    .describe('S3 presigned URLs for attachments attached to this message'),
});

export type EnhancedChatMessage = z.infer<typeof EnhancedChatMessageSchema>;

// Workflow Input/Output Schemas
export const AnalysisInputSchema = z.object({
  userPrompt: z.string().min(1),
  images: z
    .array(z.string())
    .optional()
    .describe('New images for analysis (will become current topic)'),
  currentTopicFiles: z.array(TopicFileSchema).optional().describe('Currently active topic files'),
  chatHistory: z
    .array(EnhancedChatMessageSchema)
    .optional()
    .describe('Recent conversation history with topic file context'),
  sessionId: z.string().optional().describe('Session identifier for grouping related analyses'),
});

export type AnalysisInput = z.infer<typeof AnalysisInputSchema>;

export const AnalysisOutputSchema = z.object({
  content: z.string().min(1), // Markdown response
  specialist: z.enum(['claims', 'chart', 'eligibility', 'dashboard', 'general']),
  confidence: z.number().min(0).max(1),
  newTopicFiles: z
    .array(TopicFileSchema)
    .optional()
    .describe('New topic files created from this analysis'),
  visionSummary: VisionSummarySchema.optional().describe(
    'Vision analysis summary if new images were processed'
  ),
  usedTopicFiles: z
    .array(z.string())
    .optional()
    .describe('IDs of topic files that were used in this analysis'),
});

export type AnalysisOutput = z.infer<typeof AnalysisOutputSchema>;

// Step Schemas
export const ImageProcessingOutputSchema = z.object({
  userPrompt: z.string(),
  screenContext: z
    .string()
    .optional()
    .describe('Raw vision analysis text (for backward compatibility)'),
  currentTopicFiles: z.array(TopicFileSchema).optional(),
  newTopicFiles: z.array(TopicFileSchema).optional(),
  visionSummary: VisionSummarySchema.optional(),
  chatHistory: z.array(EnhancedChatMessageSchema).optional(),
  sessionId: z.string().optional(),
});

export type ImageProcessingOutput = z.infer<typeof ImageProcessingOutputSchema>;

// Specialist selection schema
export const SpecialistContextSchema = z.object({
  userPrompt: z.string(),
  screenContext: z.string().optional(),
  currentTopicFiles: z.array(TopicFileSchema).optional(),
  visionSummary: VisionSummarySchema.optional(),
  chatHistory: z
    .array(
      z.object({
        role: z.enum(['user', 'assistant']),
        content: z.string(),
      })
    )
    .optional(),
});

export type SpecialistContext = z.infer<typeof SpecialistContextSchema>;

// Legacy schema for backward compatibility
export const VisionExtractionSchema = z.object({
  textContent: z.string().optional(),
  uiElements: z.array(z.string()).optional(),
  clinicalData: z.string().optional(),
  errorMessages: z.array(z.string()).optional(),
  statusIndicators: z.string().optional(),
});

export type VisionExtraction = z.infer<typeof VisionExtractionSchema>;

/**
 * Serializes chat history in a format optimized for LLM consumption
 * Returns a formatted string that strips binary data and provides context summaries
 */
export const serializeChatHistory = (
  messages: EnhancedChatMessage[],
  options?: {
    includeTimestamps?: boolean;
    includeAttachmentSummaries?: boolean;
    includeMetadata?: boolean;
    maxMessages?: number;
    roleLabels?: { user?: string; assistant?: string };
    sessionTitle?: string;
    sessionMetadata?: {
      totalMessages: number;
      hasScreenshots: boolean;
      primarySpecialist?: 'claims' | 'chart' | 'eligibility' | 'dashboard' | 'general';
      lastActivity: number;
      createdAt?: number;
    };
  }
): string => {
  const {
    includeTimestamps = true,
    includeAttachmentSummaries = true,
    includeMetadata = true,
    maxMessages,
    roleLabels = { user: 'Human', assistant: 'Assistant' },
    sessionTitle,
    sessionMetadata,
  } = options || {};

  const lines: string[] = [];

  // Take the most recent messages if maxMessages is specified
  const messagesToProcess = maxMessages ? messages.slice(-maxMessages) : messages;

  // Add session context if available and requested
  if (includeMetadata && (sessionTitle || sessionMetadata)) {
    lines.push('## Session Context');

    if (sessionTitle) {
      lines.push(`**Title:** ${sessionTitle}`);
    }

    if (sessionMetadata) {
      if (sessionMetadata.primarySpecialist) {
        lines.push(`**Primary Focus:** ${sessionMetadata.primarySpecialist}`);
      }

      if (sessionMetadata.createdAt && sessionMetadata.lastActivity) {
        const duration = sessionMetadata.lastActivity - sessionMetadata.createdAt;
        const hours = Math.floor(duration / (1000 * 60 * 60));
        const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));

        const durationStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
        lines.push(`**Duration:** ${durationStr}`);
      }

      if (sessionMetadata.hasScreenshots) {
        lines.push(`**Contains Screenshots:** Yes`);
      }
    }

    lines.push('');
  }

  // Add conversation history
  if (messagesToProcess.length > 0) {
    lines.push('## Conversation History');
    lines.push('');

    messagesToProcess.forEach((msg, index) => {
      const roleLabel = roleLabels[msg.role] || msg.role;
      let header = `**${roleLabel}:**`;

      // Add timestamp if available
      if (includeTimestamps && msg.timestamp) {
        const time = new Date(msg.timestamp).toLocaleTimeString();
        header += ` _(${time})_`;
      }

      lines.push(header);

      // Handle attachments/topic files
      const topicFiles = msg.currentTopicFiles || [];
      if (topicFiles.length > 0 && includeAttachmentSummaries) {
        const imageCount = topicFiles.filter(f => f.mimeType.startsWith('image/')).length;
        const otherCount = topicFiles.length - imageCount;

        const parts: string[] = [];
        if (imageCount > 0) parts.push(`${imageCount} image${imageCount > 1 ? 's' : ''}`);
        if (otherCount > 0) parts.push(`${otherCount} file${otherCount > 1 ? 's' : ''}`);

        let attachmentSummary = parts.join(', ');

        // Add source information if available
        const sources = topicFiles
          .map(f => f.metadata?.sourceType)
          .filter((s, i, arr) => s && arr.indexOf(s) === i);
        if (sources.length > 0) {
          attachmentSummary += ` (${sources.join(', ')})`;
        }

        lines.push(`_[Attachments: ${attachmentSummary}]_`);
      }

      // Add vision context if available
      if (msg.visionSummary) {
        let visionContext = msg.visionSummary.summary;
        if (msg.visionSummary.screenType) {
          visionContext += ` [Screen type: ${msg.visionSummary.screenType}]`;
        }
        lines.push(`_[Screen Analysis: ${visionContext}]_`);
      }

      lines.push(msg.content);

      // Add spacing between messages (except last)
      if (index < messagesToProcess.length - 1) {
        lines.push('');
      }
    });
  }

  return lines.join('\n');
};
